/**
 * @typedef {{
 *  readonly city: string;
 *  readonly country: string;
 *  readonly postalCode: string;
 *  readonly state: string;
 *  readonly streetAddress1: string;
 *  readonly streetAddress2: string | null;
 * }} Address
 * @typedef {{
 *  readonly date?: string | null;
 *  readonly provider: string;
 *  readonly value: {
 *   readonly high?: number | null;
 *   readonly low?: number | null;
 *   readonly mean: number;
 *  };
 * }} AvmValue
 * @typedef {{
 *  readonly valuation_id: string | null;
 *  readonly valuations?: readonly AvmValue[];
 * }} ValuationResponse
 * @typedef {{
 *  readonly address: Address;
 *  readonly agentId: string | null;
 *  readonly appTitle: string | null;
 *  readonly handleValuationId: (id: string | null) => void;
 * }} Props
 */

import React, { useEffect, useState } from 'react';
import { currency } from '@mooveguru/js-utilities';
import ErrorMessage from '../ErrorMessage';
import Loading from '../Loading';
import NoContent from '../NoContent';
import Service from '../../Service';
import { getProviderName } from './AvmView.helpers';

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
function Avm(props) {
	const service = new Service();
	const [avmList, setAvmList] = useState(
		/** @type {readonly AvmValue[]} */ ([])
	);
	const [error, setError] = useState(/** @type {string | null} */ (null));
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		storeAndGetAvms();
	}, []);

	async function storeAndGetAvms() {
		try {
			const response = /** @type {ValuationResponse} */ (
				await service.createValuation(props.address, props.agentId)
			);

			if (response.valuations?.length) {
				setAvmList(response.valuations);
			}

			props.handleValuationId(response.valuation_id);
			setIsLoading(false);
		} catch (caughtError) {
			setIsLoading(false);
			setError('A unexpected error has occurred');
		}
	}

	/**
	 * @param {AvmValue} avm
	 * @returns {string}
	 */
	function getAvmKey(avm) {
		return [
			avm.provider,
			avm.date ?? 'current',
			avm.value.mean,
			avm.value.low ?? 'none',
			avm.value.high ?? 'none',
		].join('-');
	}

	function renderAvms() {
		if (!avmList.length) {
			return (
				<NoContent
					message={
						'There currently is no data from your property to provide you with a valuation. Please contact me to get an accurate value.'
					}
					title="No Data"
				/>
			);
		}

		return avmList.map((avm) => {
			const low = avm.value.low;
			const high = avm.value.high;
			const hasLow = low !== null && low !== undefined;
			const hasHigh = high !== null && high !== undefined;

			return (
				<section className="mx-auto" key={getAvmKey(avm)}>
					<section className="flex justify-center items-center">
						{hasLow ? (
							<section className="side-message-l border-grey flex-grow text-center">
								<h3 className="h-ms text-grey-700 uppercase">
									{'low'}
								</h3>
								<p className="side-message-p font-subheader">
									{currency.convertNumberToCurrencyString(
										currency.convertCentsToDollars(low)
									)}
								</p>
							</section>
						) : null}
						<section className="message h-full px-2 py-6 border-4 border-theme w-fit-content text-center flex-grow-1">
							<p className="side-message-middle-p font-bold text-theme font-subheader">
								{currency.convertNumberToCurrencyString(
									currency.convertCentsToDollars(
										avm.value.mean
									)
								)}
							</p>
						</section>
						{hasHigh ? (
							<section className="side-message-r text-center flex-grow border-grey">
								<h3 className="h-ms text-grey-700 uppercase">
									{'high'}
								</h3>
								<p className="side-message-p font-subheader">
									{currency.convertNumberToCurrencyString(
										currency.convertCentsToDollars(high)
									)}
								</p>
							</section>
						) : null}
					</section>

					<p className="text-center my-6 text-grey-700">
						{'Estimate from '}
						<span className="font-bold">
							{getProviderName(avm.provider, props.appTitle)}
						</span>
					</p>
				</section>
			);
		});
	}

	if (error) {
		return <ErrorMessage heading="Error" message={error} />;
	}

	if (isLoading) {
		return <Loading />;
	}

	return (
		<section className="my-6 md:flex justify-between items-center avm">
			{renderAvms()}
		</section>
	);
}

export default Avm;
